import { useEffect, useState } from 'react'
import ColaboradorLayout from '@/components/ColaboradorLayout'
import { useCourses, type Course, type CourseProgress, type CoursePurchase } from '@/hooks/useCourses'
import { useAuth } from '@/hooks/useAuth'
import { useNotifications } from '@/hooks/useNotifications'
import { EVALUATION_ASPECTS } from '@/lib/evaluationAspects'
import { lumi } from '@/lib/lumi'
import {BookOpen, Star, Lock, CheckCircle, Play, Award, ShoppingCart, ArrowRight, X, BadgeCheck, Zap, CreditCard, Building2, AlertCircle, } from 'lucide-react'

const TABS = ['Todos', 'Recomendados', 'Mis cursos', 'Completados', 'Insignias', 'Mis pagos'] as const
type Tab = typeof TABS[number]

const TRANSFER_INFO = {
  clabe: '646180224800000001',
  banco: 'STP / BBVA',
  beneficiario: 'Beeznz Work S.A. de C.V.',
  concepto: 'Pago de curso premium – Beeznz Work',
}

export default function ColaboradorCursos() {
  const { user } = useAuth()
  const {
    courses, purchases, progress,
    loading,
    fetchActiveCourses, fetchPurchasesByCollaborator, fetchProgressByCollaborator,
    createPurchase, startCourse, completeCourse,
  } = useCourses()
  const { sendNotification } = useNotifications()

  const [tab, setTab] = useState<Tab>('Todos')
  const [payHistoryFilter, setPayHistoryFilter] = useState<'Todos' | 'Pendiente' | 'Pagado' | 'Rechazado'>('Todos')
  const [viewCourse, setViewCourse] = useState<Course | null>(null)
  const [payModal, setPayModal] = useState<Course | null>(null)
  const [payMethod, setPayMethod] = useState<'stripe' | 'transfer' | null>(null)
  const [transferring, setTransferring] = useState(false)
  const [collaboratorName, setCollaboratorName] = useState('')
  const [lowSkills, setLowSkills] = useState<string[]>([])
  const [msg, setMsg] = useState('')
  const [activeProgress, setActiveProgress] = useState<CourseProgress | null>(null)

  useEffect(() => {
    if (!user?.userId) return
    fetchActiveCourses()
    fetchPurchasesByCollaborator(user.userId)
    fetchProgressByCollaborator(user.userId)
    // Cargar nombre completo
    lumi.entities.collaborators.list({ filter: { userId: user.userId }, limit: 1 }).then(({ list }) => {
      if (list[0]) setCollaboratorName((list[0] as any).fullName ?? user.email ?? '')
    }).catch(() => {})
    // Cargar evaluaciones para detectar habilidades bajas
    lumi.entities.evaluations.list({
      filter: {
        collaboratorUserId: user.userId,
        $or: [
          { evaluationDirection: 'empresa_a_colaborador' },
          { evaluationDirection: { $exists: false } },
        ],
      },
      sort: { createdAt: -1 },
      limit: 20,
    }).then(({ list }) => {
      const skillMap: Record<string, number[]> = {}
      for (const ev of list as any[]) {
        const scores = ev.scores ?? {}
        for (const [key, val] of Object.entries(scores)) {
          if (!skillMap[key]) skillMap[key] = []
          skillMap[key].push(Number(val))
        }
      }
      const low: string[] = []
      for (const [key, vals] of Object.entries(skillMap)) {
        const avg = vals.reduce((a, b) => a + b, 0) / vals.length
        if (avg < 8) low.push(key)
      }
      setLowSkills(low)
    }).catch(() => {})
  }, [user?.userId])

  const paidCourseIds = new Set(purchases.filter(p => p.status === 'paid').map(p => p.courseId))
  const progressMap = new Map(progress.map(p => [p.courseId, p]))

  const isUnlocked = (c: Course) => c.type === 'free' || paidCourseIds.has(c._id)

  const recommended = courses.filter(c => lowSkills.includes(c.skill))

  const filteredCourses = (() => {
    switch (tab) {
      case 'Recomendados': return recommended
      case 'Mis cursos': return courses.filter(c => progressMap.has(c._id))
      case 'Completados': return courses.filter(c => progressMap.get(c._id)?.status === 'completed')
      case 'Insignias': return []
      default: return courses
    }
  })()

  const badges = progress.filter(p => p.status === 'completed' && p.badgeUrl)

  const handleOpenCourse = async (c: Course) => {
    if (!isUnlocked(c)) {
      setPayModal(c)
      setPayMethod(null)
      return
    }
    const existing = progressMap.get(c._id)
    if (!existing && user?.userId) {
      await startCourse(c._id, user.userId, {
        title: c.title,
        skill: c.skill,
        skillLabel: c.skillLabel,
        type: c.type,
      })
      await fetchProgressByCollaborator(user.userId)
    }
    setViewCourse(c)
    setActiveProgress(progressMap.get(c._id) ?? null)
  }

  const handleComplete = async () => {
    if (!viewCourse || !user?.userId) return
    const prog = progressMap.get(viewCourse._id)
    if (!prog || prog.status === 'completed') return
    try {
      await completeCourse(prog._id, viewCourse._id)
      await fetchProgressByCollaborator(user.userId)
      setMsg(`¡Felicidades! Completaste el curso "${viewCourse.title}" y obtuviste una insignia.`)
      setTimeout(() => setMsg(''), 5000)
      setViewCourse(null)
    } catch {
      alert('Error al registrar la finalización del curso.')
    }
  }

  const handleRequestTransfer = async () => {
    if (!payModal || !user?.userId) return
    setTransferring(true)
    try {
      await createPurchase({
        courseId: payModal._id,
        courseTitle: payModal.title,
        collaboratorUserId: user.userId,
        collaboratorName,
        collaboratorEmail: user.email,
        method: 'transfer',
        amount: payModal.price ?? 0,
        status: 'pending',
        creator: user.userId,
      })
      // Notificar al admin
      await sendNotification({
        title: `Solicitud de pago por transferencia`,
        body: `El colaborador ${collaboratorName || user.email} solicitó acceso al curso "${payModal.title}" por transferencia bancaria. Monto: $${payModal.price?.toLocaleString('es-MX')} MXN.`,
        type: 'aviso',
        targetAudience: 'admin',
        status: 'active',
      })
      setPayModal(null)
      setMsg('Solicitud de pago enviada. El admin revisará tu transferencia y desbloqueará el curso.')
      setTimeout(() => setMsg(''), 6000)
    } catch {
      alert('Error al registrar la solicitud de pago.')
    } finally {
      setTransferring(false)
    }
  }

  const TYPE_BADGE = (c: Course) =>
    c.type === 'premium'
      ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-700">Premium</span>
      : <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-bold text-blue-700">Gratis</span>

  const SKILL_BADGE = (skill: string, skillLabel?: string) => (
    <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-600">
      {skillLabel ?? skill}
    </span>
  )

  return (
    <ColaboradorLayout title="Cursos" subtitle="Mejora tus habilidades y avanza en tu carrera profesional">
      {/* Mensaje de logro */}
      {msg && (
        <div className="mb-4 flex items-center gap-2 rounded-xl bg-green-50 border border-green-200 px-4 py-3 text-sm font-medium text-green-700">
          <CheckCircle size={16} />
          {msg}
        </div>
      )}

      {/* Alerta de áreas de oportunidad */}
      {lowSkills.length > 0 && (
        <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-start gap-3">
            <Zap size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-amber-800">Tienes cursos disponibles para mejorar tus habilidades</p>
              <p className="text-xs text-amber-700 mt-0.5">
                Según tus evaluaciones, podrías reforzar:{' '}
                {lowSkills.map(k => EVALUATION_ASPECTS.find(a => a.key === k)?.label ?? k).join(', ')}.
              </p>
              <button
                onClick={() => setTab('Recomendados')}
                className="mt-2 flex items-center gap-1 text-xs font-semibold text-amber-700 hover:text-amber-900"
              >
                Ver cursos recomendados <ArrowRight size={13} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-5 flex gap-1 rounded-xl bg-gray-100 p-1 overflow-x-auto">
        {TABS.map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${tab === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            {t}
            {t === 'Recomendados' && recommended.length > 0 && (
              <span className="ml-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-bold text-white">
                {recommended.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── TAB INSIGNIAS ── */}
      {tab === 'Insignias' && (
        <div>
          {badges.length === 0 ? (
            <div className="rounded-xl bg-white border border-dashed border-gray-200 p-10 text-center">
              <Award size={36} className="mx-auto mb-3 text-gray-200" />
              <p className="text-sm text-gray-400">Aún no tienes insignias. Completa un curso para obtener tu primera.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
              {badges.map(b => {
                const course = courses.find(c => c._id === b.courseId)
                return (
                  <div key={b._id} className="flex flex-col items-center rounded-xl bg-white border border-gray-100 p-5 shadow-sm text-center">
                    <div className="relative mb-3">
                      <img src={b.badgeUrl!} alt="insignia" className="h-16 w-16 rounded-full object-cover border-4 border-amber-300 shadow" />
                      <BadgeCheck size={20} className="absolute -bottom-1 -right-1 text-amber-500 bg-white rounded-full" />
                    </div>
                    <p className="text-sm font-bold text-gray-800 leading-tight">{b.courseTitle ?? course?.title ?? 'Curso'}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{b.courseSkillLabel ?? '—'}</p>
                    {b.completedAt && (
                      <p className="mt-1 text-[11px] text-gray-300">{new Date(b.completedAt).toLocaleDateString('es-MX')}</p>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ── LISTA DE CURSOS ── */}
      {tab !== 'Insignias' && (
        <div>
          {loading && <p className="text-sm text-gray-400">Cargando cursos…</p>}
          {!loading && filteredCourses.length === 0 && (
            <div className="rounded-xl bg-white border border-dashed border-gray-200 p-10 text-center">
              <BookOpen size={32} className="mx-auto mb-3 text-gray-300" />
              <p className="text-sm text-gray-400">
                {tab === 'Recomendados'
                  ? 'No hay cursos recomendados en este momento. ¡Sigue así!'
                  : tab === 'Mis cursos'
                  ? 'Aún no has iniciado ningún curso.'
                  : tab === 'Completados'
                  ? 'Aún no has completado ningún curso.'
                  : 'No hay cursos disponibles.'}
              </p>
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredCourses.map(c => {
              const prog = progressMap.get(c._id)
              const unlocked = isUnlocked(c)
              const isRecommended = lowSkills.includes(c.skill)
              return (
                <div
                  key={c._id}
                  className={`relative flex flex-col rounded-2xl bg-white border shadow-sm overflow-hidden transition-shadow hover:shadow-md ${isRecommended ? 'border-amber-200' : 'border-gray-100'}`}
                >
                  {/* Portada */}
                  <div className="h-36 bg-gradient-to-br from-slate-700 to-slate-900 relative overflow-hidden">
                    {c.coverUrl
                      ? <img src={c.coverUrl} alt={c.title} className="h-full w-full object-cover opacity-90" />
                      : <div className="flex h-full w-full items-center justify-center"><BookOpen size={40} className="text-slate-500" /></div>
                    }
                    {/* Lock overlay para premium no comprado */}
                    {!unlocked && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                        <div className="rounded-full bg-white/20 p-3 backdrop-blur-sm"><Lock size={22} className="text-white" /></div>
                      </div>
                    )}
                    {/* Badge tipo */}
                    <div className="absolute top-2 right-2 flex gap-1">
                      {TYPE_BADGE(c)}
                    </div>
                    {/* Badge recomendado */}
                    {isRecommended && (
                      <div className="absolute top-2 left-2">
                        <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[11px] font-bold text-white flex items-center gap-1">
                          <Star size={10} /> Recomendado
                        </span>
                      </div>
                    )}
                    {/* Badge completado */}
                    {prog?.status === 'completed' && (
                      <div className="absolute bottom-2 left-2">
                        <span className="rounded-full bg-green-500 px-2 py-0.5 text-[11px] font-bold text-white flex items-center gap-1">
                          <CheckCircle size={10} /> Completado
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Contenido */}
                  <div className="flex flex-1 flex-col p-4">
                    <div className="mb-2 flex flex-wrap gap-1">
                      {SKILL_BADGE(c.skill, c.skillLabel)}
                    </div>
                    <h3 className="font-bold text-gray-900 text-sm mb-1 leading-snug">{c.title}</h3>
                    <p className="text-xs text-gray-400 line-clamp-2 flex-1">{c.description}</p>

                    {c.type === 'premium' && !unlocked && (
                      <p className="mt-2 text-sm font-bold text-amber-600">${c.price?.toLocaleString('es-MX')} MXN</p>
                    )}

                    {/* Progreso visual */}
                    {prog && prog.status === 'in_progress' && (
                      <div className="mt-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[11px] text-gray-400">En progreso</span>
                        </div>
                        <div className="h-1.5 w-full rounded-full bg-gray-100">
                          <div className="h-1.5 w-1/2 rounded-full bg-amber-400" />
                        </div>
                      </div>
                    )}

                    <button
                      onClick={() => handleOpenCourse(c)}
                      className={`mt-4 flex w-full items-center justify-center gap-2 rounded-lg py-2 text-sm font-semibold transition-colors ${
                        unlocked
                          ? prog?.status === 'completed'
                            ? 'bg-green-50 text-green-700 hover:bg-green-100'
                            : 'bg-amber-500 text-white hover:bg-amber-600'
                          : 'bg-slate-800 text-white hover:bg-slate-700'
                      }`}
                    >
                      {unlocked
                        ? prog?.status === 'completed'
                          ? <><CheckCircle size={14} /> Ver nuevamente</>
                          : <><Play size={14} /> {prog ? 'Continuar' : 'Iniciar'} curso</>
                        : <><ShoppingCart size={14} /> Adquirir curso</>
                      }
                    </button>

                    {/* Sugerencia de curso premium relacionado */}
                    {c.type === 'free' && c.relatedPremiumId && prog?.status === 'completed' && (() => {
                      const premium = courses.find(x => x._id === c.relatedPremiumId)
                      if (!premium) return null
                      return (
                        <button
                          onClick={() => { setPayModal(premium); setPayMethod(null) }}
                          className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg border border-amber-300 bg-amber-50 py-2 text-xs font-semibold text-amber-700 hover:bg-amber-100"
                        >
                          <ArrowRight size={13} /> Desbloquear versión Premium
                        </button>
                      )
                    })()}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* ── MODAL: VISOR DE CURSO ── */}
      {viewCourse && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-3xl max-h-[90vh] flex flex-col rounded-2xl bg-white shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <div>
                <h2 className="font-bold text-gray-900 text-lg leading-tight">{viewCourse.title}</h2>
                <p className="text-xs text-gray-400">{viewCourse.skillLabel ?? viewCourse.skill}</p>
              </div>
              <button onClick={() => setViewCourse(null)} className="rounded-lg p-1.5 text-gray-400 hover:text-gray-700"><X size={18} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {viewCourse.coverUrl && (
                <img src={viewCourse.coverUrl} alt="portada" className="mb-5 w-full rounded-xl object-cover max-h-48" />
              )}
              {/* Renderizado HTML seguro dentro de un iframe de sandbox o directamente */}
              <div
                className="prose prose-sm max-w-none text-gray-700 leading-relaxed"
                style={{ fontFamily: 'inherit' }}
                dangerouslySetInnerHTML={{
                  __html: viewCourse.htmlContent
                    ? viewCourse.htmlContent
                        .replace(/<script[\s\S]*?<\/script>/gi, '')
                        .replace(/on\w+="[^"]*"/gi, '')
                    : '<p style="color:#9ca3af">Este curso no tiene contenido cargado todavía.</p>'
                }}
              />
            </div>
            {/* Botón completar */}
            {progressMap.get(viewCourse._id)?.status !== 'completed' && (
              <div className="flex-shrink-0 border-t border-gray-100 px-6 py-4">
                <button
                  onClick={handleComplete}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-green-500 py-3 text-sm font-bold text-white hover:bg-green-600 transition-colors"
                >
                  <CheckCircle size={16} /> Marcar como completado y obtener insignia
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── MODAL: PAGO ── */}
      {payModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="font-bold text-gray-900">Adquirir curso premium</h2>
              <button onClick={() => { setPayModal(null); setPayMethod(null) }} className="rounded-lg p-1.5 text-gray-400 hover:text-gray-700"><X size={18} /></button>
            </div>
            <div className="p-6">
              {/* Info del curso */}
              <div className="mb-5 flex items-center gap-4 rounded-xl bg-gray-50 p-4">
                <div className="h-14 w-14 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0">
                  {payModal.coverUrl
                    ? <img src={payModal.coverUrl} alt={payModal.title} className="h-full w-full object-cover" />
                    : <div className="flex h-full w-full items-center justify-center"><BookOpen size={20} className="text-gray-400" /></div>
                  }
                </div>
                <div>
                  <p className="font-bold text-gray-900 text-sm">{payModal.title}</p>
                  <p className="text-xs text-gray-500">{payModal.skillLabel ?? payModal.skill}</p>
                  <p className="mt-1 text-base font-bold text-amber-600">${payModal.price?.toLocaleString('es-MX')} MXN</p>
                </div>
              </div>

              {!payMethod && (
                <>
                  <p className="text-sm text-gray-600 mb-4 font-medium">Elige tu método de pago:</p>
                  <div className="space-y-3">
                    <button
                      onClick={() => setPayMethod('stripe')}
                      className="flex w-full items-center gap-3 rounded-xl border-2 border-gray-200 p-4 text-left hover:border-amber-400 transition-colors"
                    >
                      <CreditCard size={22} className="text-indigo-500 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-gray-800 text-sm">Pago en línea con tarjeta</p>
                        <p className="text-xs text-gray-400">Pago seguro mediante Stripe</p>
                      </div>
                      <ArrowRight size={16} className="ml-auto text-gray-300" />
                    </button>
                    <button
                      onClick={() => setPayMethod('transfer')}
                      className="flex w-full items-center gap-3 rounded-xl border-2 border-gray-200 p-4 text-left hover:border-amber-400 transition-colors"
                    >
                      <Building2 size={22} className="text-green-500 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-gray-800 text-sm">Transferencia bancaria</p>
                        <p className="text-xs text-gray-400">Envía el pago y notificamos al admin</p>
                      </div>
                      <ArrowRight size={16} className="ml-auto text-gray-300" />
                    </button>
                  </div>
                </>
              )}

              {payMethod === 'stripe' && (
                <div className="rounded-xl bg-indigo-50 border border-indigo-100 p-5 text-center">
                  <CreditCard size={32} className="mx-auto mb-3 text-indigo-400" />
                  <p className="text-sm font-semibold text-gray-800 mb-1">Próximamente disponible</p>
                  <p className="text-xs text-gray-500 mb-4">La integración con Stripe estará habilitada pronto. Mientras tanto, puedes usar transferencia bancaria.</p>
                  <button onClick={() => setPayMethod(null)} className="text-xs font-semibold text-indigo-600 hover:underline">← Volver a opciones</button>
                </div>
              )}

              {payMethod === 'transfer' && (
                <div>
                  <div className="rounded-xl bg-green-50 border border-green-100 p-4 space-y-2 text-sm mb-4">
                    {[
                      ['CLABE', TRANSFER_INFO.clabe],
                      ['Banco', TRANSFER_INFO.banco],
                      ['Beneficiario', TRANSFER_INFO.beneficiario],
                      ['Concepto', TRANSFER_INFO.concepto],
                      ['Monto', `$${payModal.price?.toLocaleString('es-MX')} MXN`],
                    ].map(([k, v]) => (
                      <div key={k} className="flex justify-between">
                        <span className="text-gray-500 text-xs">{k}</span>
                        <span className="font-semibold text-gray-800 text-xs text-right max-w-[60%]">{v}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mb-4 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-100 px-3 py-2.5 text-xs text-amber-700">
                    <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                    Una vez que realices la transferencia, el administrador revisará tu pago y desbloqueará el curso. Recibirás una notificación.
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => setPayMethod(null)} className="flex-1 rounded-lg border border-gray-200 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50">
                      ← Volver
                    </button>
                    <button
                      onClick={handleRequestTransfer}
                      disabled={transferring}
                      className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-green-500 py-2 text-sm font-semibold text-white hover:bg-green-600 disabled:opacity-60"
                    >
                      {transferring
                        ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        : null}
                      Ya realicé el pago
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* ── TAB: MIS PAGOS ── */}
      {tab === 'Mis pagos' && (
        <div>
          <div className="mb-4 flex flex-wrap gap-2">
            {(['Todos', 'Pendiente', 'Pagado', 'Rechazado'] as const).map(f => (
              <button
                key={f}
                onClick={() => setPayHistoryFilter(f)}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                  payHistoryFilter === f ? 'bg-slate-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          {purchases.length === 0 ? (
            <div className="rounded-xl bg-white border border-dashed border-gray-200 p-10 text-center">
              <CreditCard size={32} className="mx-auto mb-3 text-gray-300" />
              <p className="text-sm text-gray-400">Aún no tienes solicitudes de pago registradas.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {(payHistoryFilter === 'Todos'
                ? purchases
                : purchases.filter(p => {
                    const map: Record<string, string> = { Pendiente: 'pending', Pagado: 'paid', Rechazado: 'rejected' }
                    return p.status === map[payHistoryFilter]
                  })
              ).map(p => (
                <div key={p._id} className="rounded-xl bg-white border border-gray-100 p-4 shadow-sm">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 text-sm truncate">{p.courseTitle ?? p.courseId}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${
                          p.status === 'paid' ? 'bg-green-100 text-green-700'
                          : p.status === 'rejected' ? 'bg-red-100 text-red-600'
                          : 'bg-amber-100 text-amber-700'
                        }`}>
                          {p.status === 'paid' ? 'Pagado' : p.status === 'rejected' ? 'Rechazado' : 'Pendiente'}
                        </span>
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                          p.method === 'transfer' ? 'bg-green-50 text-green-700' : 'bg-indigo-50 text-indigo-700'
                        }`}>
                          {p.method === 'transfer' ? 'Transferencia' : 'Stripe'}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-base font-bold text-amber-600">${p.amount?.toLocaleString('es-MX')} MXN</p>
                      <p className="text-[11px] text-gray-400">{new Date(p.createdAt).toLocaleDateString('es-MX')}</p>
                    </div>
                  </div>
                  {p.status === 'pending' && (
                    <div className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-100 px-3 py-2 text-xs text-amber-700">
                      <AlertCircle size={13} className="flex-shrink-0 mt-0.5" />
                      Tu solicitud de pago está en revisión. Recibirás una notificación cuando sea procesada.
                    </div>
                  )}
                  {p.status === 'paid' && p.validatedAt && (
                    <p className="mt-2 text-[11px] text-gray-400">Acreditado el {new Date(p.validatedAt).toLocaleDateString('es-MX')}</p>
                  )}
                  {p.status === 'rejected' && (
                    <div className="mt-3 flex items-start gap-2 rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-xs text-red-600">
                      <AlertCircle size={13} className="flex-shrink-0 mt-0.5" />
                      Tu pago fue rechazado. Contáctanos si necesitas asistencia.
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

    </ColaboradorLayout>
  )
}
